import { DataFarmType } from 'src/app/domainObjects/dataFarmType';
import { RowExtractorTransform, RowExtractorTransformConfig, RowMissesOptionExtractor, SequenceOption, ConsolidationLevel, SmartSampleMode } from 'src/app/domainObjects/rowExtractorTransform';
import { PreviewDataImportRequest } from 'src/app/messaging/previewImportDataRequest';
import { RowExtractorPreviewDataResponse } from 'src/app/messaging/rowExtractorPreviewDataResponse';
import { ConfigService } from 'src/app/services/config/config.service';
import { DataTransformConfigViewModel } from 'src/app/viewModels/dataTransformConfigViewModel';
import { TransformerViewModel } from '../../../transformer-list/transformer-list.component';
import { RowExtractorRuleViewModel } from './extractorRuleViewModel';
import { ExtractorEndRuleType, ExtractorStartRuleType } from 'src/app/messaging/engine';
import { ColumnFilter } from 'src/app/domainObjects/columnFilter';
import { TransformDetailViewModelBase } from '../../../dataTransformConfig/transformers/base/base-transformer-ui/transformDetailViewModelBase';
import { DataDictionaryService } from 'src/app/services/dataDictionary/data-dictionary.service';
import { ValidationInfo, RequiredFieldValidation } from 'src/app/objectModel/validationInfo';
import { TransformerParametersViewModel } from '../transformer-parameters/transformerParametersViewModel';
import { IncludeInfo } from 'src/app/domainObjects/includeInfo';
import { TransformType } from 'src/app/domainObjects/transformType';

export class StartRuleTypeUtil {
  static toString(ruleType: ExtractorStartRuleType): string {
      switch (ruleType) {
        case ExtractorStartRuleType.startsWith: return 'Starts With';
        case ExtractorStartRuleType.startsAfter: return 'Starts After';
        case ExtractorStartRuleType.beginning: return 'Beginning';
        case ExtractorStartRuleType.justCopy: return 'Just Copy';
        case ExtractorStartRuleType.lengthBetween: return 'Length Between';
        case ExtractorStartRuleType.ending: return 'Ending (Deprecated)';
      }
  }
}

export class EndRuleTypeUtil {
    static toString(ruleType: ExtractorEndRuleType): string {
        switch (ruleType) {
          case ExtractorEndRuleType.endsWith: return 'Ends With';
          case ExtractorEndRuleType.endsBefore: return 'Ends Before';
          case ExtractorEndRuleType.endsAtEnd: return 'Ends at End';
          case ExtractorEndRuleType.length: return 'Length';
          case ExtractorEndRuleType.ending: return 'Ending';
          case ExtractorEndRuleType.findPhrases: return 'Find Phrases';
        }
  }
}

export class RowExtractorViewModel extends TransformDetailViewModelBase<RowExtractorTransform> {
    farmType: DataFarmType;
    sourceColumn: string;
    targetColumn: string;
    //userColumn: string;
    rowMissesOption:RowMissesOptionExtractor;
    missesNamed: string;
    runAll: boolean;

    bufferRows: number;
    threads: number;
    defMinLength: number;
    defMaxLength: number;
    defMaxSearch: number;
    searchWildCard: string;
    resetRows: boolean; 
    sequenceOption:SequenceOption;
    consolidation:ConsolidationLevel;
    smartSampleMode: SmartSampleMode;

    rules: Array<RowExtractorRuleViewModel> = [];
    // Preview options
    rows: number;
    sampleStart: number;
    sampleEnd: number;
    // Status
    hitCount?: number;
    missCount?: number;
    hitRatio?: number;
    hitRatioDisplay?: string;
    variantCount?: number;
  
    request: PreviewDataImportRequest;
    response: RowExtractorPreviewDataResponse;
  
    constructor(
      dataDictService: DataDictionaryService,
      private configService: ConfigService,
      configViewModel: DataTransformConfigViewModel,
      transformViewModel: TransformerViewModel) {

        super(dataDictService, configViewModel, transformViewModel);

        const transformConfig = this.transform.transformConfig;
        this.parameters=new TransformerParametersViewModel();
        if (transformConfig) {
          //console.log('Existing transformConfig used.');
          //console.log(transformConfig);
          this.farmType = transformConfig.farmType ?? transformViewModel.transform.dataFarmType;
          this.transformConfigId = transformConfig.transformConfigId;
          this.sourceColumn = transformConfig.defSourceField;
          this.targetColumn = transformConfig.defTargetField;
          //this.deleteMisses = transformConfig.delMisses;
          this.sampleEnd = transformConfig.sampleEnd + 1;
          this.sampleStart = transformConfig.sampleStart + 1;
          this.runAll = transformConfig.runAll;
          this.sortInfo = transformConfig.sortInfo;
          if(transformConfig.includeInResults){
            this.includeInResults = transformConfig.includeInResults.map(item =>new IncludeInfo(item));
            this.parameters.includeInResults=this.includeInResults;
          }
          //parameters
          this.parameters.sourceColumn= transformConfig.defSourceField;
          this.parameters.targetColumn= transformConfig.defTargetField;
          this.parameters.userColumn= transformConfig.userField;
          this.parameters.timeColumn= transformConfig.timestampField;
          this.parameters.sortInfo= transformConfig.sortInfo;
          //this.parameters.includeInResults= transformConfig.in;
          this.parameters.sampleStart = transformConfig.sampleStart+ 1;
          this.parameters.sampleEnd = transformConfig.sampleEnd+ 1;
          this.parameters.runAll= transformConfig.runAll;
          //this.parameters.deleteMisses = transformConfig.delMisses;
          this.parameters.missName= transformConfig.missesNamed;
          //other parameters
          this.missesNamed = transformConfig.missesNamed;
          this.rowMissesOption = transformConfig.rowMissesOption;
          // advanced settings
          this.bufferRows = transformConfig.bufferRows;
          this.threads = transformConfig.threads;
          this.searchWildCard = transformConfig.searchWildCard;
          //this.multiTargetColumn = transformConfig.multiColumnTarget;
          this.resetRows = transformConfig.resetRows;
          this.defMaxLength = transformConfig.defMaxLength;
          this.defMinLength = transformConfig.defMinLength;
          this.defMaxSearch = transformConfig.defMaxSearch;
          this.sequenceOption    = transformConfig.sequenceOption;
          this.consolidation     = transformConfig.consolidation ?? ConsolidationLevel.standard;
          this.smartSampleMode   = transformConfig.smartSampleMode ?? SmartSampleMode.per_field;

          if (transformConfig.rules) {
            this.rules = transformConfig.rules.map(r => new RowExtractorRuleViewModel(r));
          }
        }
    }

    override configureValidationInfo(): ValidationInfo[] {
      const infos = [];
      infos.push( new RequiredFieldValidation('userField', 'User Field'));
      infos.push( new RequiredFieldValidation('timestampField', 'Timestamp Field'));
      infos.push( new RequiredFieldValidation('bufferRows', 'Buffer Rows'));
      infos.push( new RequiredFieldValidation('threads', 'Threads (0 > Auto)'));
      infos.push( new RequiredFieldValidation('defMinLength', 'Default Min Length'));
      infos.push( new RequiredFieldValidation('defMaxLength', 'Default Max Length'));
      infos.push( new RequiredFieldValidation('defMaxSearch', 'Default Max Search'));
      infos.push( new RequiredFieldValidation('searchWildCard', 'Wild Card'));
      
      return infos;
    }

    reindexRules(): void {
      for (let i = 0; i < this.rules.length; i++) {
        this.rules[i].index = i + 1;
      }
      this.rules.sort((a, b) => a.index - b.index);
    }

    override onTransformRunCompleted(response: RowExtractorPreviewDataResponse): void {
      // A cache-hit run carries no stats at all (they're deliberately stripped
      // before a CachedProcessingResult is persisted -- see
      // project_stats_vs_fullrun_separation memory). Nothing has changed since
      // whatever last populated the stats currently on screen, so they're still
      // accurate -- leave them exactly as they are instead of wiping them blank.
      if (this.runAll) {
        if (
          !response?.previewStats ||
          !response?.totalStats ||
          !response.totalStats.globalStat ||
          !response.totalStats.ruleStats ||
          !response.previewStats.ruleStats
        ) {
          return;
        }
      } else {
        if (
          !response?.previewStats ||
          !response.previewStats.globalStat ||
          !response.previewStats.ruleStats
        ) {
          return;
        }
      }

      // Clear all stats prior to rebinding new values
      this.hitCount     = null;
      this.hitRatio     = null;
      this.missCount    = null;
      this.variantCount = null;
      this.hitRatioDisplay ="0.00%";
      this.rows = 0;
      this.rules.forEach( r => {
        r.hits           = null;
        r.sampleHits     = null;
        r.groupHits       = null;
        r.sampleGroupHits = null;
      });

      if (this.runAll) {
        // Map Transform Global and Rule stats
        this.hitCount = response.totalStats?.globalStat.rowHits;
        
        if (response.totalStats?.globalStat.hitRate) {
          this.hitRatio = response.totalStats.globalStat.hitRate;
          this.hitRatioDisplay = `${(this.hitRatio * 100).toFixed(2)}%`;
        }

        this.missCount    = response.totalStats?.globalStat.rowMisses;
        this.variantCount = response.totalStats?.globalStat.groupHits;

        if (response.totalStats?.ruleStats) {
          for (let i = 0; i < this.rules.length; i++) {
            const stat = response.totalStats.ruleStats.find(r => r.ruleIndex === i);
            this.rules[i].hits     = stat?.rowHits;
            this.rules[i].groupHits = stat?.groupHits;
          }
        }
        //sampleHits
        if (response.previewStats?.ruleStats) {
          for (let i = 0; i < this.rules.length; i++) {
            const stat = response.previewStats.ruleStats.find(r => r.ruleIndex === i);
            this.rules[i].sampleHits     = stat?.rowHits;
            this.rules[i].sampleGroupHits = stat?.groupHits;
          }
        }
      } else {
        // Map Transform Global and Rule stats
        this.hitCount = response.previewStats.globalStat.rowHits;
        
        if (response.previewStats.globalStat.hitRate) {
          this.hitRatio = response.previewStats.globalStat.hitRate;
          this.hitRatioDisplay = `${(this.hitRatio * 100).toFixed(2)}%`;
        }

        this.missCount    = response.previewStats.globalStat.rowMisses;
        this.variantCount = response.previewStats.globalStat.groupHits;

        if (response.previewStats?.ruleStats) {
          for (let i = 0; i < this.rules.length; i++) {
            const stat = response.previewStats.ruleStats.find(r => r.ruleIndex === i);
            this.rules[i].sampleHits     = stat?.rowHits;
            this.rules[i].sampleGroupHits = stat?.groupHits;
          }
        }
      }

      this.rows = this.runAll ? response.currentData?.totalRows : response.activeData?.totalRows;
    }

    override toTransformConfig(): RowExtractorTransformConfig {
      const config = new RowExtractorTransformConfig();
      config.farmType = this.farmType;
      config.transformType = TransformType.rowExtractor;
      config.defSourceField=this.parameters.sourceColumn;
      config.defTargetField=this.parameters.targetColumn;
      if(this.parameters.sortInfo){
        if(this.parameters.sortInfo.length==0){
          config.sortInfo =undefined;
        }else{
          config.sortInfo=this.parameters.sortInfo;
        }
      }
      if(this.parameters.includeInResults){
        if(this.parameters.includeInResults.length==0){
          config.includeInResults =undefined;
        }else{
          config.includeInResults =this.parameters.includeInResults.map(item => item.includeField);
        }
      }
      config.sampleStart =this.parameters.sampleStart-1;
      config.sampleEnd =this.parameters.sampleEnd-1;
      config.rowMissesOption =this.rowMissesOption;
      config.missesNamed =this.missesNamed;
      config.runAll=this.runAll;

      config.bufferRows = this.bufferRows;
      config.threads = this.threads;
      config.defMinLength = this.defMinLength;
      config.defMaxLength = this.defMaxLength;
      config.defMaxSearch = this.defMaxSearch;
      config.searchWildCard = this.searchWildCard;
      config.resetRows = this.resetRows;
      config.sequenceOption   = this.sequenceOption;
      config.consolidation    = this.consolidation;
      config.smartSampleMode  = this.smartSampleMode ?? SmartSampleMode.per_field;

      config.rules = this.rules.map(r => {
        return r.toRowExtractorRule();
      });

      return config;
    }
  }